import { Box, Stack } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useOutsideClick } from "../../../hooks/useOutSideClick";
import { CustomButton } from "../CustomButton";
import { FilterIcon } from "../../images/FilterIcon";
import { AccordionBody } from "./AccordionBody";
import { DataSources, KeyRoles } from "../../../types/enums";
import { EllipsisTooltip } from "../CustomTooltip";
import { keyRolesItems } from "../../../types/genereal";
import { KeyRoleMapper } from "../../company/KeyRoleMapper";
import { ResetAllFiltersButton } from "../../features/ResetAllFiltersButton";
import { useStateWithSearchParams } from "../../../hooks";
import { QUERY_SEPARATOR } from "../../../constant";

const NO_PERSONA_MATCH_ID = "no_persona_match";
const NO_PERSONA_MATCH_LABEL = "No Persona Match";

type Props = {
  isDisabled: boolean;
  dataSourceList: DataSources[];
  personas: { id: string; name: string; actual_count: number }[]; // ← добавить actual_count
};
type SearchParams = {
  roles?: string;
  data_source?: string;
  personas?: string;
};

export const AccountFiltersDropdown = ({
  isDisabled,
  dataSourceList,
  personas,
}: Props) => {
  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedAccordion, setSelectedAccordion] = useState<string>("");
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    roles: undefined,
    data_source: undefined,
    personas: undefined,
  });

  const closeHandler = () => {
    setIsOpen(false);
  };

  useOutsideClick({
    elementRef: dropdownRef,
    onOutsideClick: closeHandler,
    isOpened: isOpen,
    wrapperRef: dropdownWrapperRef,
  });

  const updatePosition = () => {
    const rect = dropdownWrapperRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        left: rect.left + window.scrollX,
        top: rect.top + window.scrollY + 60,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  const selected =
    (searchDict.roles?.split(QUERY_SEPARATOR).length ?? 0) +
    (searchDict.data_source?.split(QUERY_SEPARATOR).length ?? 0) +
    (searchDict.personas?.split(QUERY_SEPARATOR).length ?? 0);

  return (
    <Stack
      ref={dropdownWrapperRef}
      sx={{
        position: "relative",
        flexDirection: "row",
        gap: "12px",
      }}
    >
      <CustomButton
        onClick={() => setIsOpen(!isOpen)}
        variant="text"
        type="button"
        sx={{
          gap: 0,
          textWrap: "nowrap",
          color: "#1C3C6C",
          backgroundColor: `${isOpen ? "#F1F1F1" : "transparent"}`,
          "&:hover": {
            backgroundColor: "#F1F1F1",
          },
          "& .MuiButton-startIcon": {
            marginRight: "4px",
          },
          height: "36px",
        }}
        startIcon={<FilterIcon color={isDisabled ? "#F1F1F1" : "#1C3C6C"} />}
        customSize="fit"
        disabled={isDisabled}
      >
        <Stack direction="row" alignItems="center" gap="16px">
          Filters
          {!!selected && (
            <Box
              sx={{
                backgroundColor: "#1B3967",
                color: "white",
                borderRadius: "20px",
                fontSize: "12px",
                lineHeight: "20px",
                paddingX: "6px",
                minWidth: "20px",
                textAlign: "center",
              }}
            >
              {selected}
            </Box>
          )}
        </Stack>
      </CustomButton>
      {!isDisabled && !!selected && <ResetAllFiltersButton />}

      {isOpen &&
        ReactDOM.createPortal(
          <Box
            ref={dropdownRef}
            sx={{
              backgroundColor: "transparent",
              position: "absolute",
              left: position.left,
              top: position.top,
              paddingBottom: "24px",
            }}
          >
            <Box
              sx={{
                backgroundColor: "white",
                boxShadow: "0px 4px 15px 0px #00000040",
                borderRadius: "8px",
                paddingY: "8px",
                width: "260px",
              }}
            >
              <AccordionBody
                setSelectedAccordion={setSelectedAccordion}
                selectedAccordion={selectedAccordion}
                key={"roles"}
                query={"roles"}
                items={Object.keys(KeyRoles)}
                placeholder={"Roles"}
                renderItem={(value) => (
                  <Stack padding="8px" alignItems="flex-start">
                    <EllipsisTooltip
                      tooltip={keyRolesItems[value as KeyRoles].tooltip}
                      hide={false}
                    >
                      <KeyRoleMapper item={value as KeyRoles} />
                    </EllipsisTooltip>
                  </Stack>
                )}
              />
              {dataSourceList.length > 1 && (
                <AccordionBody
                  setSelectedAccordion={setSelectedAccordion}
                  selectedAccordion={selectedAccordion}
                  key={"data_source"}
                  query={"data_source"}
                  items={dataSourceList}
                  placeholder={"Data Source"}
                />
              )}
              {personas.length > 0 && (
                <AccordionBody
                  setSelectedAccordion={setSelectedAccordion}
                  selectedAccordion={selectedAccordion}
                  key={"personas"}
                  query={"personas"}
                  items={[
                    ...personas
                      .filter((p) => p.actual_count > 0)
                      .map((p) => p.id),
                    NO_PERSONA_MATCH_ID,
                  ]}
                  placeholder={"Personas"}
                  renderItem={(value) => (
                    <Stack padding="8px">
                      <span>
                        {value === NO_PERSONA_MATCH_ID
                          ? NO_PERSONA_MATCH_LABEL
                          : (personas.find((p) => p.id === value)?.name ??
                            value)}
                      </span>
                    </Stack>
                  )}
                />
              )}
            </Box>
          </Box>,
          document.body,
        )}
    </Stack>
  );
};
